import React from 'react';
import { FileText, Sparkles, Search, RotateCcw, ChevronRight } from 'lucide-react';

export default function EssayStartProgressive() {
  return (
    <div className="min-h-screen bg-stone-50 p-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-slate-900 font-bold text-2xl mb-2">
            Start Your Essay
          </h1>
          <p className="text-stone-600 text-sm">
            Choose the approach that matches where you are right now
          </p>
        </div>

        {/* Recommended Path - Maximum Visual Weight */}
        <div className="mb-8 bg-white border-l-4 border-l-gold-500 border border-slate-900 rounded-xl overflow-hidden shadow-sm">
          <div className="p-8">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold-100 rounded-lg flex items-center justify-center">
                  <Search className="w-5 h-5 text-gold-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-slate-900 font-bold text-lg">
                      Find Ideas
                    </h2>
                  </div>
                  <p className="text-stone-600 text-sm">
                    Best starting point for most students
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gold-400 rounded-lg">
                <svg className="w-3.5 h-3.5 text-slate-900" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-slate-900 text-xs font-bold uppercase tracking-wide">
                  Start Here
                </span>
              </div>
            </div>

            <p className="text-slate-700 text-sm mb-6 leading-relaxed">
              Not sure what to write about? Explore prompts and discover compelling topics through AI-powered conversations. Your assessment provides the foundation for generating authentic ideas.
            </p>

            <button className="w-full bg-slate-900 text-white px-6 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors">
              Start Exploring Ideas
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Alternative Paths - Clean Grid */}
        <div className="grid grid-cols-3 gap-4">
          
          {/* Essay Builder */}
          <button className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-colors text-left group">
            <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
              <Sparkles className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-slate-900 font-bold text-sm mb-2">
              Essay Builder
            </h3>
            <p className="text-stone-600 text-xs leading-relaxed mb-3">
              Step-by-step prompts help you develop your story. Best when you already have a topic.
            </p>
            <span className="text-indigo-600 text-xs font-medium">
              Guided approach →
            </span>
          </button>

          {/* Blank Draft */}
          <button className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-colors text-left group">
            <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
              <FileText className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-slate-900 font-bold text-sm mb-2">
              Blank Draft
            </h3>
            <p className="text-stone-600 text-xs leading-relaxed mb-3">
              Start with a clean page. Full creative freedom when you know what you want to write.
            </p>
            <span className="text-teal-600 text-xs font-medium">
              Direct approach →
            </span>
          </button>

          {/* Continue Draft */}
          <button className="bg-white border border-slate-300 rounded-lg p-6 hover:border-slate-900 transition-colors text-left group">
            <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-slate-900 transition-colors">
              <RotateCcw className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="text-slate-900 font-bold text-sm mb-2">
              Continue Draft
            </h3>
            <p className="text-stone-600 text-xs leading-relaxed mb-3">
              Pick up where you left off. 15 drafts ready to refine or expand.
            </p>
            <span className="text-magenta-600 text-xs font-medium">
              Resume work →
            </span>
          </button>

        </div>

        {/* Context Helper */}
        <div className="mt-6 p-4 bg-white border border-stone-200 rounded-lg">
          <p className="text-stone-600 text-xs">
            <span className="font-medium text-slate-700">Not sure?</span> Start with Find Ideas to discover what makes your story compelling. Once you have a topic, Essay Builder helps you develop it with structure. You can always switch approaches.
          </p>
        </div>

      </div>
    </div>
  );
}